import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import { api } from "@/lib/api";
import { defaultContent, mergeContent } from "@/lib/siteContent";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CalendarCheck, ChatCircleText, Gear, SignOut, House, Trash } from "@phosphor-icons/react";

const TABS = [
  { id: "appointments", label: "Appointments", icon: CalendarCheck },
  { id: "messages", label: "Messages", icon: ChatCircleText },
  { id: "content", label: "CMS Content", icon: Gear },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("appointments");

  if (user === null) return <div className="min-h-screen grid place-items-center text-[#475569]">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[#F4F7FA] flex">
      <aside className="hidden md:flex w-64 shrink-0 flex-col bg-[#0A192F] text-white p-6 sticky top-0 h-screen">
        <div className="flex items-center gap-3 mb-10">
          <span className="w-9 h-9 rounded-full bg-white text-[#0A192F] grid place-items-center font-display text-lg">R</span>
          <span className="font-display text-lg">Admin Panel</span>
        </div>
        <nav className="space-y-1 flex-1">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-colors ${tab === t.id ? "bg-white text-[#0A192F]" : "text-white/70 hover:bg-white/10"}`}>
              <t.icon size={18} weight="duotone" /> {t.label}
            </button>
          ))}
        </nav>
        <Link to="/" className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-white/70 hover:bg-white/10" data-testid="admin-view-site"><House size={18} /> View Website</Link>
        <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-white/70 hover:bg-white/10"><SignOut size={18} /> Sign Out</button>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="md:hidden flex items-center justify-between bg-[#0A192F] text-white px-5 py-4">
          <span className="font-display text-lg">Admin</span>
          <div className="flex gap-2">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} className={`p-2 rounded-lg ${tab === t.id ? "bg-white text-[#0A192F]" : "text-white/70"}`}><t.icon size={18} /></button>
            ))}
            <button onClick={logout} className="p-2 text-white/70"><SignOut size={18} /></button>
          </div>
        </header>
        <main className="p-6 md:p-12 max-w-6xl">
          {tab === "appointments" && <Appointments />}
          {tab === "messages" && <Messages />}
          {tab === "content" && <ContentEditor />}
        </main>
      </div>
    </div>
  );
}

function Appointments() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/appointments").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => { await api.patch(`/appointments/${id}/status`, { status }); load(); };
  const del = async (id) => { await api.delete(`/appointments/${id}`); load(); toast.success("Deleted"); };

  return (
    <div>
      <h1 className="font-display text-4xl text-[#0A192F]">Appointments</h1>
      <p className="text-[#475569] mt-2">{rows.length} total requests</p>
      <div className="mt-8 space-y-4" data-testid="admin-appointments-list">
        {rows.length === 0 && <p className="text-[#64748B]">No appointment requests yet.</p>}
        {rows.map((r) => (
          <div key={r.id} className="bg-white border border-[#E2E8F0] rounded-2xl p-6" data-testid="admin-appointment-row">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h3 className="font-display text-2xl text-[#0A192F]">{r.name}</h3>
                <p className="text-sm text-[#475569] mt-1">{r.phone} · {r.email || "no email"}</p>
                <p className="text-sm text-[#475569]">Preferred: {r.preferred_date || "—"} {r.preferred_time}</p>
                {r.reason && <p className="text-sm text-[#0A192F] mt-2"><b>Reason:</b> {r.reason}</p>}
                {r.message && <p className="text-sm text-[#475569] mt-1">{r.message}</p>}
                <p className="font-mono text-xs text-[#64748B] mt-2">{new Date(r.created_at).toLocaleString()}</p>
              </div>
              <div className="flex items-center gap-2">
                <select value={r.status} onChange={(e) => setStatus(r.id, e.target.value)} className="text-sm border border-[#E2E8F0] rounded-lg px-3 py-2 bg-white" data-testid="admin-appointment-status">
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="closed">Closed</option>
                </select>
                <button onClick={() => del(r.id)} className="p-2 text-[#64748B] hover:text-red-500 transition-colors" data-testid="admin-appointment-delete"><Trash size={18} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Messages() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/contact").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <div>
      <h1 className="font-display text-4xl text-[#0A192F]">Messages</h1>
      <div className="mt-8 space-y-4" data-testid="admin-messages-list">
        {rows.length === 0 && <p className="text-[#64748B]">No contact messages yet.</p>}
        {rows.map((r) => (
          <div key={r.id} className="bg-white border border-[#E2E8F0] rounded-2xl p-6">
            <h3 className="font-display text-xl text-[#0A192F]">{r.name}</h3>
            <p className="text-sm text-[#475569]">{r.email} {r.phone}</p>
            <p className="text-[#0A192F] mt-2">{r.message}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ContentEditor() {
  const { content, setContent } = useContent();
  const [draft, setDraft] = useState(() => mergeContent(defaultContent, content));
  const [saving, setSaving] = useState(false);

  const upd = (section, key) => (e) => setDraft({ ...draft, [section]: { ...draft[section], [key]: e.target.value } });
  const updField = (key) => (e) => setDraft({ ...draft, about: { ...draft.about, fields: { ...draft.about.fields, [key]: e.target.value } } });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content", { data: draft });
      setContent(draft);
      toast.success("Content saved. Live site updated.");
    } catch (_) { toast.error("Could not save."); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-content-editor">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="font-display text-4xl text-[#0A192F]">CMS Content</h1>
        <button onClick={save} disabled={saving} data-testid="admin-content-save" className="rounded-full bg-[#0A192F] text-white px-6 py-3 hover:bg-[#112240] transition-colors disabled:opacity-60">
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>

      <Section title="Hero">
        <Field label="Eyebrow" value={draft.hero.eyebrow} onChange={upd("hero", "eyebrow")} />
        <Field label="Headline" value={draft.hero.headline} onChange={upd("hero", "headline")} />
        <Field label="Subheadline" value={draft.hero.sub} onChange={upd("hero", "sub")} area />
      </Section>

      <Section title="About">
        <Field label="Introduction" value={draft.about.intro} onChange={upd("about", "intro")} area />
        <Field label="Approach to Care" value={draft.about.approach} onChange={upd("about", "approach")} area />
        <Field label="Medical Qualification" value={draft.about.fields.qualification} onChange={updField("qualification")} />
        <Field label="Years of Experience" value={draft.about.fields.experience} onChange={updField("experience")} />
        <Field label="Professional Memberships" value={draft.about.fields.memberships} onChange={updField("memberships")} />
        <Field label="Areas of Clinical Expertise" value={draft.about.fields.expertise} onChange={updField("expertise")} />
        <Field label="Languages Spoken" value={draft.about.fields.languages} onChange={updField("languages")} />
      </Section>

      <Section title="Contact & Clinic">
        <Field label="Phone" value={draft.contact.phone} onChange={upd("contact", "phone")} />
        <Field label="WhatsApp" value={draft.contact.whatsapp} onChange={upd("contact", "whatsapp")} />
        <Field label="Email" value={draft.contact.email} onChange={upd("contact", "email")} />
        <Field label="Clinic Address" value={draft.contact.address} onChange={upd("contact", "address")} area />
        <Field label="Consultation Hours" value={draft.contact.hours} onChange={upd("contact", "hours")} />
      </Section>
    </div>
  );
}

function Field({ label, value, onChange, area }) {
  return (
    <div className="space-y-2">
      <Label className="label text-[0.55rem]">{label}</Label>
      {area ? <Textarea value={value} onChange={onChange} rows={3} /> : <Input value={value} onChange={onChange} className="h-11" />}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-10 bg-white border border-[#E2E8F0] rounded-2xl p-6">
      <h2 className="font-display text-2xl text-[#0A192F] mb-5">{title}</h2>
      <div className="grid sm:grid-cols-2 gap-5">{children}</div>
    </div>
  );
}
